import {
  GET_SOCIEDADES_SUCURSALES,
  LIST_BODEGAS_EMPRESA,
  LIST_BODEGAS_EMPRESA_CON_SEGMENTACION,
} from "@/apollo/queries/epp";

import {
  CREATE_BODEGA,
  CREATE_BODEGA_CON_SEGMENTACION,
  UPDATE_BODEGA,
  UPDATE_BODEGA_CON_SEGMENTACION,
  DELETE_BODEGA,
} from "@/apollo/mutations/epp";

import { exMut, exQue } from "@/utils/gql";

// Sucursales y sociedades por bodega (cero o más de cada una). En false todo
// funciona como antes y no se piden ni se envían. Necesita el backend 65479501 o
// posterior (CreateBodega y UpdateBodega con id_sucursales e id_sociedades, más
// el campo `sociedades` en ListBodegasEmpresa): con true y un backend anterior,
// la lista de bodegas queda vacía.
// El área de la bodega (id_area_fk) queda fuera: el backend no expone cómo
// listar las áreas.
export const BODEGAS_CON_SUCURSALES_Y_SOCIEDADES = true;

// Ids numéricos, sin repetidos ni vacíos
const idsValidos = (ids = []) => [...new Set(ids.map(Number))].filter((id) => id > 0);

/**
 * Deja la segmentación de una bodega en una forma estable para las vistas.
 *
 * Hace falta por dos cosas que el backend devuelve tal cual salen de la base:
 * - Dentro de `sociedades`, los `id_sucursal` llegan como String mientras que
 *   los de `id_sucursales` llegan como Int. Acá quedan todos en Int, para poder
 *   compararlos contra los ids que usa el resto del admin.
 * - Una bodega guardada solo con sucursales, sin ninguna sociedad, vuelve con
 *   `sociedades` vacío y sus sucursales solo en la lista plana. Esas quedan en
 *   `sucursalesSueltas` para que la vista las pueda mostrar igual, en vez de
 *   desaparecer al leer únicamente la jerarquía.
 *
 * @param {Object} bodega - un elemento crudo de ListBodegasEmpresa.
 * @returns {Object} la misma bodega con `sociedades` normalizado y
 * `sucursalesSueltas` agregado. Ambos son siempre arreglos.
 */
export function normalizarBodega(bodega) {
  const sociedades = (bodega?.sociedades || []).map((sociedad) => ({
    id_sociedad: Number(sociedad?.id_sociedad),
    nombre_sociedad: String(sociedad?.nombre_sociedad || "").trim(),
    id_sucursales: idsValidos(sociedad?.id_sucursales),
    sucursales: (sociedad?.sucursales || []).map((sucursal) => ({
      id_sucursal: Number(sucursal?.id_sucursal),
      nombre_sucursal: String(sucursal?.nombre_sucursal || "").trim(),
    })),
  }));

  const enJerarquia = new Set(sociedades.flatMap((sociedad) => sociedad.id_sucursales));

  return {
    ...bodega,
    sociedades,
    sucursalesSueltas: idsValidos(bodega?.id_sucursales).filter((id) => !enJerarquia.has(id)),
  };
}

/**
 * Si la bodega ya limita a quién atiende (tiene sociedades o sucursales sueltas).
 *
 * Importa por cómo reparte el stock el backend (hasBodegasSSConfigured y
 * getBodegasByUsuarioSucursalSociedad en .SolicitudInventario/controllers/
 * encargadoHelper.js):
 * - Mientras ninguna bodega de la empresa esté limitada, todas atienden a todos.
 * - En cuanto una lo está, cada trabajador recibe solo de las que coinciden con
 *   su sociedad y, si la bodega marca sucursales, también con su sucursal. Las
 *   bodegas sin segmentación dejan de entregar.
 * - Las sucursales sueltas no coinciden con nadie, pero sí cuentan como
 *   limitación.
 *
 * @param {Object} bodega - ya normalizada con normalizarBodega.
 * @returns {Boolean}
 */
export const tieneSegmentacion = (bodega) =>
  Boolean(bodega?.sociedades?.length || bodega?.sucursalesSueltas?.length);

/**
 * Deja la respuesta de GetSociedadesSucursales en una forma estable para las vistas:
 * ids en Int (el backend los manda como String) y sin entradas sin id válido.
 *
 * @param {Array} sociedades - la respuesta cruda.
 * @returns {Array<{ id_sociedad: number, nombre_sociedad: string,
 *   sucursales: Array<{ id_sucursal: number, nombre_sucursal: string }> }>}
 */
export function normalizarSociedadesSucursales(sociedades) {
  return (Array.isArray(sociedades) ? sociedades : [])
    .map((sociedad) => ({
      id_sociedad: Number(sociedad?.id_sociedad),
      nombre_sociedad: String(sociedad?.nombre_sociedad || "").trim(),
      sucursales: (Array.isArray(sociedad?.sucursales) ? sociedad.sucursales : [])
        .map((sucursal) => ({
          id_sucursal: Number(sucursal?.id_sucursal),
          nombre_sucursal: String(sucursal?.nombre_sucursal || "").trim(),
        }))
        .filter((sucursal) => sucursal.id_sucursal > 0),
    }))
    .filter((sociedad) => sociedad.id_sociedad > 0);
}

/**
 * Sucursales que se le ofrecen a una sociedad al armar una bodega.
 *
 * Con relación (GetSociedadesSucursales): las que el backend asocia a esa sociedad,
 * es decir, donde tiene trabajadores. Esas son además las únicas parejas que pueden
 * coincidir cuando el backend reparte stock por sociedad y sucursal. A eso se suman
 * las que la bodega ya tiene marcadas en la sociedad aunque el backend no las
 * asocie (una bodega guardada con el modelo plano asociaba cada sucursal a cada
 * sociedad): si no se mostraran quedarían ocultas, pero se reenviarían al guardar.
 * Van con `sinTrabajadores: true` y dejan de aparecer al desmarcarlas.
 *
 * Sin relación (la consulta falló, o ninguna sociedad trae sucursales porque los
 * usuarios de la empresa no las tienen cargadas): todas las de la empresa, como
 * antes de GetSociedadesSucursales. Acotar con una relación vacía le dejaría sin
 * nada que ofrecer a una empresa que sí tiene sucursales.
 *
 * @param {Object} datos
 * @param {Array} datos.relacion - state.sociedadesSucursales, ya normalizado.
 * @param {Array<{ value, label }>} datos.todas - todas las sucursales de la empresa
 * (usersModule.workplaceArr).
 * @param {number|string} datos.idSociedad
 * @param {Array<number|string>} datos.marcadas - sucursales de la bodega en esa sociedad.
 * @param {string} datos.respaldo - cómo llamar a una sucursal sin nombre conocido.
 * @returns {Array<{ value: number, label: string, sinTrabajadores: boolean }>}
 */
export function sucursalesParaSociedad({
  relacion = [],
  todas = [],
  idSociedad,
  marcadas = [],
  respaldo = "Sucursal",
} = {}) {
  const todasEnInt = (Array.isArray(todas) ? todas : []).map((sucursal) => ({
    value: Number(sucursal.value),
    label: sucursal.label,
    sinTrabajadores: false,
  }));

  const hayRelacion = (Array.isArray(relacion) ? relacion : []).some(
    (sociedad) => sociedad.sucursales.length,
  );
  if (!hayRelacion) return todasEnInt;

  const id = Number(idSociedad);
  const propias = (
    relacion.find((sociedad) => sociedad.id_sociedad === id)?.sucursales || []
  ).map((sucursal) => ({
    value: sucursal.id_sucursal,
    label: sucursal.nombre_sucursal,
    sinTrabajadores: false,
  }));
  const asociadas = new Set(propias.map((sucursal) => sucursal.value));

  const sueltas = idsValidos(marcadas)
    .filter((idSucursal) => !asociadas.has(idSucursal))
    .map((idSucursal) => ({
      value: idSucursal,
      label:
        todasEnInt.find((sucursal) => sucursal.value === idSucursal)?.label ||
        `${respaldo} #${idSucursal}`,
      sinTrabajadores: true,
    }));

  return [...propias, ...sueltas];
}

export const BodegaState = {
  bodegas: [],
  bodegasLoading: false,
  // Relación sociedad -> sucursales con trabajadores (getSociedadesSucursales). En []
  // mientras no llega o si la consulta falló: BodegasModal lo toma como "sin
  // relación" y ofrece todas las sucursales
  sociedadesSucursales: [],
  sociedadesSucursalesLoading: false,
};

export const BodegaMutations = {
  setBodegas(state, bodegas) {
    state.bodegas = bodegas;
  },
  setBodegasLoading(state, isLoading) {
    state.bodegasLoading = isLoading;
  },
  setSociedadesSucursales(state, sociedades) {
    state.sociedadesSucursales = sociedades;
  },
  setSociedadesSucursalesLoading(state, isLoading) {
    state.sociedadesSucursalesLoading = isLoading;
  },
};

export const BodegaActions = {
  async getBodegas({ commit }) {
    commit("setBodegasLoading", true);
    try {
      const response = await exQue(
        BODEGAS_CON_SUCURSALES_Y_SOCIEDADES
          ? LIST_BODEGAS_EMPRESA_CON_SEGMENTACION
          : LIST_BODEGAS_EMPRESA,
        {},
        true,
      );
      const lista = Array.isArray(response) ? response : [];
      // Sin segmentación la respuesta no trae nada que normalizar, y agregarle
      // campos vacíos solo confundiría a quien lea el state
      commit(
        "setBodegas",
        BODEGAS_CON_SUCURSALES_Y_SOCIEDADES ? lista.map(normalizarBodega) : lista,
      );
      return response;
    } finally {
      commit("setBodegasLoading", false);
    }
  },
  // Carga la relación sociedad -> sucursales para BodegasModal. Siempre en red: se
  // arma con los trabajadores, que cambian. Si falla (el backend todavía no tiene
  // GetSociedadesSucursales, p. ej. `master` sin desplegar) queda en [] y el modal
  // ofrece todas las sucursales, como antes. exQue ya deja el error en consola; no
  // se avisa con un toast porque nada deja de funcionar. Devuelve la relación.
  async getSociedadesSucursales({ commit }) {
    commit("setSociedadesSucursalesLoading", true);
    try {
      const response = await exQue(GET_SOCIEDADES_SUCURSALES, {}, true);
      const relacion = normalizarSociedadesSucursales(response);
      commit("setSociedadesSucursales", relacion);
      return relacion;
    } catch {
      commit("setSociedadesSucursales", []);
      return [];
    } finally {
      commit("setSociedadesSucursalesLoading", false);
    }
  },
  async createBodega({ dispatch, commit }, nombre) {
    commit("setBodegasLoading", true);
    try {
      const response = await exMut(
        CREATE_BODEGA,
        { nombre },
        "Bodega creada correctamente",
        "No se pudo crear la bodega",
      );

      if (response) {
        await dispatch("getBodegas");
      }

      return response;
    } finally {
      commit("setBodegasLoading", false);
    }
  },
  async updateBodega({ dispatch, commit }, { id_bodega, nombre }) {
    commit("setBodegasLoading", true);
    try {
      const response = await exMut(
        UPDATE_BODEGA,
        { id_bodega, nombre },
        "Bodega actualizada correctamente",
        "No se pudo actualizar la bodega",
      );

      if (response) {
        await dispatch("getBodegas");
      }

      return response;
    } finally {
      commit("setBodegasLoading", false);
    }
  },
  // Crea o edita una bodega con su segmentación, en una sola llamada. Solo con
  // BODEGAS_CON_SUCURSALES_Y_SOCIEDADES.
  //
  // `sociedades` es [{ id_sociedad, id_sucursales }]; puede ir vacío (bodega sin
  // segmentar) y cada entrada puede tener id_sucursales vacío, que para el
  // backend significa "toda la sociedad". Las entradas sin id_sociedad válido se
  // descartan: el input del backend lo declara GraphQLNonNull y rechazaría todo.
  //
  // Devuelve el id de la bodega, o null si hay que reintentar.
  async guardarBodega(
    { dispatch, commit },
    { bodega = null, nombre = "", sociedades = [] } = {},
  ) {
    const nombreLimpio = String(nombre || "").trim();
    if (!BODEGAS_CON_SUCURSALES_Y_SOCIEDADES || !nombreLimpio) return null;
    const segmentacion = (Array.isArray(sociedades) ? sociedades : [])
      .map((item) => ({
        id_sociedad: Number(item?.id_sociedad),
        id_sucursales: idsValidos(item?.id_sucursales),
      }))
      .filter((item) => item.id_sociedad > 0);

    commit("setBodegasLoading", true);
    try {
      const idBodega = Number(bodega?.id_bodega) || null;
      const guardada = idBodega
        ? await exMut(
            UPDATE_BODEGA_CON_SEGMENTACION,
            { id_bodega: idBodega, nombre: nombreLimpio, sociedades: segmentacion },
            "Bodega actualizada correctamente",
            "No se pudo actualizar la bodega",
          )
        : await exMut(
            CREATE_BODEGA_CON_SEGMENTACION,
            { nombre: nombreLimpio, sociedades: segmentacion },
            "Bodega creada correctamente",
            "No se pudo crear la bodega",
          );
      if (!guardada) return null;

      // Ya se guardó: si falla recargar la lista, exQue lo deja en consola
      await dispatch("getBodegas").catch(() => null);
      return Number(guardada.id_bodega) || idBodega;
    } finally {
      commit("setBodegasLoading", false);
    }
  },
  async deleteBodega({ dispatch, commit }, id_bodega) {
    commit("setBodegasLoading", true);
    try {
      const response = await exMut(
        DELETE_BODEGA,
        { id_bodega },
        "Bodega eliminada correctamente",
        "No se pudo eliminar la bodega",
      );

      if (response) {
        await dispatch("getBodegas");
      }

      return response;
    } finally {
      commit("setBodegasLoading", false);
    }
  },
};
